import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';

export function SettingsPanel() {
  const [settings, setSettings] = useState({
    enabled: true,
    require_approval: false,
    max_per_day: 10,
    max_per_week: 50,
    min_match_score: 70,
    job_types: [] as string[],
    remote_preference: 'any',
    salary_minimum: 0,
    excluded_companies: [] as string[],
    resume_strategy: 'ai_optimized',
    cover_letter_strategy: 'custom',
    cover_letter_tone: 'professional',
    cover_letter_length: 'medium',
    follow_up_enabled: false,
    follow_up_days: 7,
    include_portfolio: false,
    portfolio_url: '',
    include_linkedin: false,
    linkedin_url: '',
    include_github: false,
    github_url: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('auto_apply_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('auto_apply_settings')
        .upsert({
          user_id: user.id,
          ...settings,
        });

      if (error) throw error;

      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div>Loading settings...</div>;
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2">Customize Your AI Application Strategy</h2>
        <p className="text-muted-foreground">Configure how AI applies to jobs on your behalf</p>
      </div>

      <div className="space-y-4">
        {/* Application Preferences */}
        <Collapsible defaultOpen>
          <CollapsibleTrigger className="w-full">
            <Card className="p-6 cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Application Preferences</h3>
                <ChevronDown className="h-5 w-5" />
              </div>
            </Card>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="p-6 mt-2 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="smart-apply">Smart Apply to new matching jobs</Label>
                  <p className="text-sm text-muted-foreground">Automatically apply when new jobs match your criteria</p>
                </div>
                <Switch
                  id="smart-apply"
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="require-approval">Require my approval before each application</Label>
                  <p className="text-sm text-muted-foreground">Review applications before they're submitted</p>
                </div>
                <Switch
                  id="require-approval"
                  checked={settings.require_approval}
                  onCheckedChange={(checked) => setSettings({ ...settings, require_approval: checked })}
                />
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Maximum applications per day: {settings.max_per_day}</Label>
                  <Slider
                    value={[settings.max_per_day]}
                    onValueChange={(value) => setSettings({ ...settings, max_per_day: value[0] })}
                    max={50}
                    min={1}
                    step={1}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>Maximum applications per week: {settings.max_per_week}</Label>
                  <Slider
                    value={[settings.max_per_week]}
                    onValueChange={(value) => setSettings({ ...settings, max_per_week: value[0] })}
                    max={200}
                    min={1}
                    step={5}
                    className="mt-2"
                  />
                </div>
              </div>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Job Criteria */}
        <Collapsible>
          <CollapsibleTrigger className="w-full">
            <Card className="p-6 cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Job Criteria</h3>
                <ChevronDown className="h-5 w-5" />
              </div>
            </Card>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="p-6 mt-2 space-y-6">
              <div>
                <Label>Minimum match score: {settings.min_match_score}%</Label>
                <Slider
                  value={[settings.min_match_score]}
                  onValueChange={(value) => setSettings({ ...settings, min_match_score: value[0] })}
                  max={100}
                  min={50}
                  step={5}
                  className="mt-2"
                />
              </div>

              <div className="space-y-2">
                <Label>Job Types</Label>
                <div className="space-y-2">
                  {['Full-time', 'Part-time', 'Contract', 'Internship', 'Temporary'].map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={type}
                        checked={settings.job_types.includes(type.toLowerCase())}
                        onCheckedChange={(checked) => {
                          const types = checked
                            ? [...settings.job_types, type.toLowerCase()]
                            : settings.job_types.filter((t) => t !== type.toLowerCase());
                          setSettings({ ...settings, job_types: types });
                        }}
                      />
                      <label htmlFor={type} className="text-sm cursor-pointer">
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Remote Preference</Label>
                <Select
                  value={settings.remote_preference}
                  onValueChange={(value) => setSettings({ ...settings, remote_preference: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="remote">Remote Only</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="on-site">On-site</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Minimum Salary (optional)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 50000"
                  value={settings.salary_minimum || ''}
                  onChange={(e) => setSettings({ ...settings, salary_minimum: parseInt(e.target.value) || 0 })}
                  className="mt-2"
                />
              </div>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Resume Strategy */}
        <Collapsible>
          <CollapsibleTrigger className="w-full">
            <Card className="p-6 cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Resume Strategy</h3>
                <ChevronDown className="h-5 w-5" />
              </div>
            </Card>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="p-6 mt-2 space-y-6">
              <div>
                <Label>Resume Approach</Label>
                <Select
                  value={settings.resume_strategy}
                  onValueChange={(value) => setSettings({ ...settings, resume_strategy: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ai_optimized">Use AI-optimized resume for each job</SelectItem>
                    <SelectItem value="base">Always use my base resume</SelectItem>
                    <SelectItem value="library">Let AI choose best resume from my library</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="portfolio">Include Portfolio Link</Label>
                  <Switch
                    id="portfolio"
                    checked={settings.include_portfolio}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_portfolio: checked })}
                  />
                </div>
                {settings.include_portfolio && (
                  <Input
                    placeholder="https://yourportfolio.com"
                    value={settings.portfolio_url}
                    onChange={(e) => setSettings({ ...settings, portfolio_url: e.target.value })}
                  />
                )}

                <div className="flex items-center justify-between">
                  <Label htmlFor="linkedin">Include LinkedIn Profile</Label>
                  <Switch
                    id="linkedin"
                    checked={settings.include_linkedin}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_linkedin: checked })}
                  />
                </div>
                {settings.include_linkedin && (
                  <Input
                    placeholder="https://linkedin.com/in/yourprofile"
                    value={settings.linkedin_url}
                    onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })}
                  />
                )}

                <div className="flex items-center justify-between">
                  <Label htmlFor="github">Include GitHub Profile</Label>
                  <Switch
                    id="github"
                    checked={settings.include_github}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_github: checked })}
                  />
                </div>
                {settings.include_github && (
                  <Input
                    placeholder="https://github.com/yourusername"
                    value={settings.github_url}
                    onChange={(e) => setSettings({ ...settings, github_url: e.target.value })}
                  />
                )}
              </div>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Cover Letter Strategy */}
        <Collapsible>
          <CollapsibleTrigger className="w-full">
            <Card className="p-6 cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Cover Letter Strategy</h3>
                <ChevronDown className="h-5 w-5" />
              </div>
            </Card>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="p-6 mt-2 space-y-6">
              <div>
                <Label>Cover Letter Approach</Label>
                <Select
                  value={settings.cover_letter_strategy}
                  onValueChange={(value) => setSettings({ ...settings, cover_letter_strategy: value })}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Generate custom cover letter for each job</SelectItem>
                    <SelectItem value="template">Use my template cover letter</SelectItem>
                    <SelectItem value="none">No cover letter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.cover_letter_strategy === 'custom' && (
                <>
                  <div>
                    <Label>Tone</Label>
                    <Select
                      value={settings.cover_letter_tone}
                      onValueChange={(value) => setSettings({ ...settings, cover_letter_tone: value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Length</Label>
                    <Select
                      value={settings.cover_letter_length}
                      onValueChange={(value) => setSettings({ ...settings, cover_letter_length: value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short (150 words)</SelectItem>
                        <SelectItem value="medium">Medium (250 words)</SelectItem>
                        <SelectItem value="long">Long (400 words)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Follow-up Settings */}
        <Collapsible>
          <CollapsibleTrigger className="w-full">
            <Card className="p-6 cursor-pointer hover:bg-accent/50 transition-colors">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Follow-up Settings</h3>
                <ChevronDown className="h-5 w-5" />
              </div>
            </Card>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="p-6 mt-2 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="follow-up">Enable Automatic Follow-ups</Label>
                  <p className="text-sm text-muted-foreground">Send follow-up emails after no response</p>
                </div>
                <Switch
                  id="follow-up"
                  checked={settings.follow_up_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, follow_up_enabled: checked })}
                />
              </div>

              {settings.follow_up_enabled && (
                <div>
                  <Label>Send follow-up after (days): {settings.follow_up_days}</Label>
                  <Slider
                    value={[settings.follow_up_days]}
                    onValueChange={(value) => setSettings({ ...settings, follow_up_days: value[0] })}
                    max={14}
                    min={1}
                    step={1}
                    className="mt-2"
                  />
                </div>
              )}
            </Card>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <Button onClick={handleSave} disabled={saving} className="mt-6" size="lg">
        <Save className="mr-2 h-5 w-5" />
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
}
